import { Typography } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React from 'react';
import ActionText from '../../core/ActionText';
import MenuSelector from '../../core/MenuSelector';
import CommonIriDisplay from '../../application-core/CommonIriDisplay';
import InfoLink from '../InfoLink';
import Paging from '../Paging';
import { globalStyles as styles } from '../style/gstheme';
import { BN_NAMESPACE, USER_NAMESPACE } from '../utils/constants';
import namespaces from '../utils/namespaces';
import ReportDatatypeDisplay from './ReportDatatypeDisplay';
import ReportVisualisationContainer from './ReportVisualisationContainer';

const namespaceKeys = Object.keys(namespaces);

class ReportResultsViewer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            pageSize: 50,
            size: props.resultsData.results.bindings.length
        };
    }

    static getDerivedStateFromProps(props, state) {
        if (props.resultsData.results.bindings.length !== state.size) {
            return {
                size: props.resultsData.results.bindings.length,
                page: 1
            };
        }
        return null;
    }

    checkForPrefix(iri) {
        const ns = namespaceKeys.find(prefix => iri && iri.startsWith(prefix) ? true : false);
        return ns ? namespaces[ns] + ":" + iri.substring(ns.length) : iri;
    }

    handlePagingChange = (page) => this.setState({ page });

    handleSizeChange = (pageSize) => this.setState({ page: 1, pageSize });

    render() {

        const { classes, resultsData, graph, components, defaultLang, viz, showDetail, projectUsers, project, projectType } = this.props;
        const { page, pageSize } = this.state;

        const columns = resultsData.head ? resultsData.head.vars : [];
        const results = resultsData.results ? resultsData.results.bindings : [];
        const displayStartIndex = (pageSize * page) - pageSize + 1;
        const displayResults = results.slice(displayStartIndex - 1, displayStartIndex - 1 + pageSize);
        const visualiationData = cloneDeep(displayResults);
        const componentIDs = components ? components.map(c => c.id) : [];
        visualiationData.forEach(di => {
            Object.keys(di).forEach(dik => {
                if (di[dik].type === "uri") {
                    const displayValue = di[dik].value;
                    if (displayValue.startsWith(USER_NAMESPACE) && projectUsers && projectUsers.some(pu => pu.value === displayValue)) {
                        di[dik].value = projectUsers.find(pu => pu.value === displayValue).label;
                    }
                }
            });
        });
        const maxDisplayCount = Math.min(displayStartIndex + pageSize - 1, results.length);
        const pageCount = Math.floor((results.length + pageSize - 1) / pageSize);

        if (maxDisplayCount === 0) {
            return <div style={{ height: '130px' }}>
                <ActionText dataTest="no-report-results" offset="80px">There are no results for this report.</ActionText>;
            </div>;
        }

        return (
            <div>
                <div style={{ display: 'flex', marginBottom: 20, marginTop: 12 }}>
                    {results.length > 50 &&
                        <React.Fragment>
                            <div style={{ flex: 1, marginTop: 25 }}>
                                <Typography data-test="showing" variant="body2" className={classes.showing}>Showing {displayStartIndex} to {maxDisplayCount} of {results.length} results.</Typography>
                            </div>
                            <div style={{ width: 130, alignSelf: 'right' }}>
                                <MenuSelector
                                    dataTest="page-size-selector"
                                    label="Results to Show"
                                    handleChange={this.handleSizeChange}
                                    selected={pageSize.toString()}
                                    hintWord="results size"
                                    options={[
                                        {
                                            value: 50,
                                            label: "50"
                                        },
                                        {
                                            value: 100,
                                            label: "100"
                                        },
                                        {
                                            value: 250,
                                            label: "250"
                                        },
                                        {
                                            value: 500,
                                            label: "500"
                                        }
                                    ]} />
                            </div>
                        </React.Fragment>
                    }
                </div>
                {viz && <ReportVisualisationContainer defaultLang={defaultLang} data={visualiationData} viz={viz} />}
                <div style={{ overflow: 'scroll' }}>
                    <Table data-test="result-viewer" size="small" style={{ maxWidth: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell component="th" className={classes.tableHeading} style={{ width: 50 }}>No.</TableCell>
                                {columns.map((column, index) => <TableCell
                                    key={index}
                                    component="th"
                                    className={classes.tableHeading}>{column.replace(/_/g, ' ')}</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayResults.map((result, index) =>
                                <TableRow
                                    data-test={"row-" + index}
                                    key={"row-" + index}
                                    classes={{
                                        root: classes.infoRow
                                    }}>
                                    <TableCell
                                        component="th"
                                        className={classes.infoCellNo}>{displayStartIndex + index}
                                    </TableCell>
                                    {columns.map((column, colIndex) => {
                                        if (!result[column]) {
                                            return <TableCell
                                                key={colIndex}
                                                component="td"
                                                className={classes.tableCell}></TableCell>;
                                        }
                                        let displayValue = result[column].value;
                                        let displayLang = result[column]["xml:lang"];
                                        let dt = result[column].datatype;
                                        let cellContents;
                                        if (result[column].type === "uri") {
                                            if (componentIDs.includes(displayValue)) {
                                                cellContents = <CommonIriDisplay
                                                    id={displayValue}
                                                    components={components}
                                                    projectIRI={project}
                                                    compact={true}
                                                    projectType={projectType}
                                                    handleNotAvailable={true}
                                                    context={displayValue}
                                                    graph={graph}
                                                    wrap={false}
                                                    viewLang={defaultLang}
                                                    defaultLang={defaultLang} />;
                                            }
                                            else if (displayValue.startsWith(USER_NAMESPACE)
                                                && projectUsers.some(pu => pu.value === displayValue)) {
                                                cellContents = projectUsers.find(pu => pu.value === displayValue).label;
                                            }
                                            else if (displayValue.startsWith(BN_NAMESPACE)) {
                                                cellContents = "Blank node (substituted)";
                                            }
                                            else {
                                                cellContents = <InfoLink
                                                    id={displayValue}
                                                    defaultLang={defaultLang}
                                                    graph={graph}
                                                    components={components}
                                                    isReadOnly={true} />;
                                            }
                                        }
                                        else if (result[column].type === "bnode") {
                                            cellContents = <Typography variant="body2">Blank node: {displayValue}</Typography>;
                                        }
                                        else {
                                            cellContents = <ReportDatatypeDisplay
                                                value={displayValue}
                                                showDetail={showDetail}
                                                datatype={dt}
                                                lang={displayLang} />;
                                        }
                                        return <TableCell
                                            key={colIndex}
                                            data-test={"row-" + index + "-col-" + colIndex}
                                            component="td"
                                            style={{ minWidth: 50 }}
                                            className={classes.infoCell}
                                        >
                                            {cellContents}
                                        </TableCell>;
                                    }
                                    )}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <Paging page={page} pageCount={pageCount} handlePageChange={this.handlePagingChange} pageItemsRange={5} />
            </div>
        );
    }
}

ReportResultsViewer.defaulProps = {
    showDetail: false
};

ReportResultsViewer.propTypes = {
    resultsData: PropTypes.object,
    graph: PropTypes.string.isRequired,
    project: PropTypes.string.isRequired,
    viz: PropTypes.array,
    showDetail: PropTypes.bool,
    projectType: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    components: PropTypes.array,
    projectUsers: PropTypes.array,
    queryType: PropTypes.string,
    classes: PropTypes.object
};

export default withStyles(styles)(ReportResultsViewer);